/**
 * Multi-client MCP config detection and writer.
 *
 * Supports:
 *  - Cursor: .cursor/mcp.json in cwd (project-level)
 *  - Claude Desktop: ~/Library/Application Support/Claude/claude_desktop_config.json (macOS)
 *                    %APPDATA%/Claude/claude_desktop_config.json (Windows)
 *
 * The writer reads existing config, merges the new server entry (never
 * overwrites existing entries), and writes back. Falls back to printing
 * a snippet for unsupported OS or unknown formats.
 */

import { existsSync, readFileSync, writeFileSync, mkdirSync } from "node:fs";
import { join, dirname } from "node:path";
import { homedir, platform } from "node:os";

export interface McpClientInfo {
  name: string;
  configPath: string;
}

const SERVER_ENTRY_KEY = "Product Brain";
const LEGACY_ENTRY_KEY = "productbrain";

/**
 * Canonical npx package specifier. Update here when exiting beta.
 * Frontend mirror: src/lib/constants/mcp.ts
 * Business rule: BR-84 (Chain)
 */
export const MCP_NPX_PACKAGE = "@productbrain/mcp@beta";

function buildServerEntry(apiKey: string) {
  return {
    command: "npx",
    args: ["-y", MCP_NPX_PACKAGE],
    env: { PRODUCTBRAIN_API_KEY: apiKey },
  };
}

// ── Detection ───────────────────────────────────────────────────────────

function getCursorConfigPath(): string {
  return join(process.cwd(), ".cursor", "mcp.json");
}

function getClaudeDesktopConfigPath(): string | null {
  const os = platform();
  if (os === "darwin") {
    return join(
      homedir(),
      "Library",
      "Application Support",
      "Claude",
      "claude_desktop_config.json",
    );
  }
  if (os === "win32") {
    const appData = process.env.APPDATA ?? join(homedir(), "AppData", "Roaming");
    return join(appData, "Claude", "claude_desktop_config.json");
  }
  // Linux: no official Claude Desktop location yet
  return null;
}

export function resolveClient(name: "Cursor" | "Claude Desktop"): McpClientInfo | null {
  if (name === "Cursor") {
    return { name, configPath: getCursorConfigPath() };
  }
  const configPath = getClaudeDesktopConfigPath();
  return configPath ? { name, configPath } : null;
}

// ── Writing ─────────────────────────────────────────────────────────────

function readJsonSafe(path: string): Record<string, any> {
  if (!existsSync(path)) return {};
  try {
    return JSON.parse(readFileSync(path, "utf-8"));
  } catch {
    return {};
  }
}

/**
 * Write or merge the Product Brain server entry into a client config file.
 * Migrates legacy "productbrain" key to "Product Brain" when present.
 * Returns true if the config was written, false if already present.
 */
export async function writeClientConfig(
  client: McpClientInfo,
  apiKey: string,
): Promise<boolean> {
  const config = readJsonSafe(client.configPath);

  const serversKey = "mcpServers";
  if (!config[serversKey]) config[serversKey] = {};

  // Migrate legacy "productbrain" key or update existing Product Brain with new API key
  if (config[serversKey][LEGACY_ENTRY_KEY]) {
    const legacy = config[serversKey][LEGACY_ENTRY_KEY];
    config[serversKey][SERVER_ENTRY_KEY] = {
      ...buildServerEntry(apiKey),
      env: { ...legacy.env, PRODUCTBRAIN_API_KEY: legacy.env?.PRODUCTBRAIN_API_KEY ?? apiKey },
    };
    delete config[serversKey][LEGACY_ENTRY_KEY];
  } else {
    const existing = config[serversKey][SERVER_ENTRY_KEY];
    config[serversKey][SERVER_ENTRY_KEY] = existing
      ? { ...existing, env: { ...existing.env, PRODUCTBRAIN_API_KEY: apiKey } }
      : buildServerEntry(apiKey);
  }

  const dir = dirname(client.configPath);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true });
  }

  writeFileSync(client.configPath, JSON.stringify(config, null, 2) + "\n", "utf-8");
  return true;
}
